import {
  BASE_MINUTES,
  DEFAULT_BASE_MINUTES,
  DEFAULT_ROLE_FACTOR,
  ROLE_FACTOR,
  WORKING_DAYS_PER_WEEK,
} from './capacityConstants';
import { TASK_DISPLAY_STATUS } from './constants';
import { daysUntil, getTaskDisplayStatus, isTaskBlockedStatus } from './helpers';

export const roleFactor = (role) => ROLE_FACTOR[role] ?? DEFAULT_ROLE_FACTOR;

export const estMinutes = (task, role) => {
  const base = BASE_MINUTES[task?.typeId] ?? DEFAULT_BASE_MINUTES;
  return Math.round(base * roleFactor(role));
};

export const isCommittable = (task) =>
  getTaskDisplayStatus(task) !== TASK_DISPLAY_STATUS.Done && !isTaskBlockedStatus(task);

export const inHorizon = (task, horizon) => {
  const days = daysUntil(task?.due);
  if (Number.isNaN(days)) return false;
  return horizon === 'day' ? days <= 0 : days <= WORKING_DAYS_PER_WEEK + 1;
};

export const availableMinutes = (horizon, dailyMinutes, weeklyMinutes) =>
  horizon === 'day' ? dailyMinutes : weeklyMinutes;

export const committedMinutes = (tasks, horizon, role) =>
  (tasks || []).reduce((sum, task) => {
    if (!isCommittable(task)) return sum;
    if (!inHorizon(task, horizon)) return sum;
    return sum + estMinutes(task, role);
  }, 0);

export const utilisation = (committed, available) => {
  if (available <= 0) return committed > 0 ? Infinity : 0;
  return Math.max(0, committed / available);
};

export const band = (util, hasBreach = false) => {
  if (hasBreach) return 'breach';
  return util >= 0.85 ? 'over' : 'ok';
};

export const deadlineFeasibility = (tasks, role, dailyMinutes) => {
  const open = (tasks || [])
    .filter(isCommittable)
    .map((task) => {
      const rawDays = daysUntil(task.due);
      return {
        task,
        rawDays,
        days: Math.max(0, rawDays),
        minutes: estMinutes(task, role),
      };
    })
    .sort((a, b) => a.rawDays - b.rawDays);

  let cumulative = 0;
  return open.map(({ task, days, rawDays, minutes }) => {
    cumulative += minutes;
    const capacityByDue = dailyMinutes * Math.max(1, days + 1);
    return {
      taskId: task.id,
      willMiss: cumulative > capacityByDue,
      due: task.due,
      daysUntilDue: rawDays,
      cumulativeMinutes: cumulative,
      capacityByDue,
    };
  });
};

export const hasBreach = (tasks, role, dailyMinutes) =>
  deadlineFeasibility(tasks, role, dailyMinutes).some((result) => result.willMiss);
